// The host admin page (/admin -> #/admin).
//   - Microsoft sign-in -> the session cookie carrying hostId (GET /api/auth/login)
//   - Session probe via GET /api/me (401 => show the sign-in button); the host it
//     returns drives the dashboard header, the shareable booking link, and logout.
//   - Settings editor for the HostConfig: booking types CRUD, weekly working
//     hours, buffers, minimum notice, rolling window -> PUT /api/admin/config
//   - Connect calendars: Microsoft comes with sign-in; Google is an optional
//     extra connect (GET /api/connect/google).
//   - Logout control -> POST /api/auth/logout, then back to the sign-in prompt.
// Implements: ADR-0002 sections 1, 3, 4, 5.

import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { DEFAULT_RETENTION_DAYS, enableSyncDefaults } from '@numaco-meet/core';
import type { ConnectionId, SyncDirection } from '@numaco-meet/core';
import { ApiError } from '../api/types.js';
import type {
  AdminConfigResponse,
  BookingType,
  HostConfig,
  SignedInHost,
  SyncHealth,
} from '../api/types.js';
import { api } from '../api/client.js';
import { relativeTime } from '../lib/time.js';
import { bookingAbsoluteLink, bookingPathLink } from '../lib/router.js';
import { BrandShell } from '../components/BrandShell.js';
import { Spinner, StateBlock } from '../components/feedback.js';
import {
  DAY_NAMES,
  cloneConfig,
  formToWeek,
  newBookingTypeId,
  sanitizeSlug,
  validateEventSlug,
  weekToForm,
  type DayForm,
  type WeekForm,
} from '../lib/admin.js';

type AuthState = 'checking' | 'signed-out' | 'signed-in';

export function AdminPage(): JSX.Element {
  const [auth, setAuth] = useState<AuthState>('checking');
  const [host, setHost] = useState<SignedInHost | null>(null);

  // Probe the session via GET /api/me; 401 => show the sign-in button. The host
  // it returns is what the dashboard renders around.
  const probe = useCallback(async () => {
    setAuth('checking');
    try {
      const res = await api.getMe();
      setHost(res.host);
      setAuth('signed-in');
    } catch {
      setHost(null);
      setAuth('signed-out');
    }
  }, []);

  useEffect(() => {
    void probe();
  }, [probe]);

  async function logout(): Promise<void> {
    try {
      await api.logout();
    } catch {
      // Even if the call fails, drop the local session view so the user is not
      // stuck on the dashboard; the cookie clear is best-effort.
    }
    setHost(null);
    setAuth('signed-out');
  }

  return (
    <BrandShell lede="Host settings." sub="Manage your booking types, hours, and connected calendars.">
      <header className="page-head admin-head">
        <div>
          <span className="eyebrow">Admin</span>
          <h1 className="page-title">Numaco Meet settings</h1>
        </div>
        {auth === 'signed-in' && host ? (
          <button type="button" className="btn-link" onClick={() => void logout()}>
            Sign out
          </button>
        ) : null}
      </header>

      {auth === 'checking' ? (
        <div className="state">
          <Spinner />
          <p>Checking your session…</p>
        </div>
      ) : auth === 'signed-in' && host ? (
        <Dashboard host={host} />
      ) : (
        <SignInPrompt />
      )}
    </BrandShell>
  );
}

// ---------------- Dashboard ----------------

function Dashboard({ host }: { host: SignedInHost }): JSX.Element {
  return (
    <div className="rise">
      <BookingLinkCard slug={host.slug} displayName={host.displayName} />
      <ConfigEditor />
    </div>
  );
}

/** The host's shareable booking link, with a copy control. */
function BookingLinkCard({ slug, displayName }: { slug: string; displayName: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const absolute = bookingAbsoluteLink(slug);
  const path = bookingPathLink(slug);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(absolute);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore silently.
    }
  }

  return (
    <section className="admin-section booking-link-card">
      <h2>Your booking page</h2>
      <p className="sec-desc">
        Share this link, signed in as <strong>{displayName}</strong>. People pick a time and you
        get an invite.
      </p>
      <div className="booking-link-row">
        <a className="booking-link" href={path}>
          {absolute}
        </a>
        <button type="button" className="btn btn-ghost" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </section>
  );
}

// ---------------- Sign in ----------------

function SignInPrompt(): JSX.Element {
  return (
    <div className="form admin-login rise">
      <p className="page-desc" style={{ margin: 0 }}>
        Sign in with your Numaco Microsoft account to set up your booking page. This
        connects your Numaco calendar in the same step.
      </p>
      <a className="btn btn-primary btn-block" href={api.loginUrl()}>
        Sign in with Microsoft
      </a>
    </div>
  );
}

// ---------------- Config editor ----------------

type EditorState = 'loading' | 'ready' | 'error';

function ConfigEditor(): JSX.Element {
  const [state, setState] = useState<EditorState>('loading');
  const [loadError, setLoadError] = useState<string>();
  const [config, setConfig] = useState<HostConfig | null>(null);
  const [syncHealth, setSyncHealth] = useState<AdminConfigResponse['syncHealth']>();
  const [week, setWeek] = useState<WeekForm>({});
  // Per-event working-hours forms for private events (ADR-0003), keyed by booking
  // type id. Editable HH:MM strings, mirroring the host `week` form; converted to
  // WeeklyHours on save. One window per weekday for v1 (split days out of scope).
  const [eventWeeks, setEventWeeks] = useState<Record<string, WeekForm>>({});
  // Per-event "use custom hours" sub-toggle, keyed by booking type id. DEFAULT
  // OFF (absent === false): an uncustomized private event stores NO workingHours
  // and thus inherits the host schedule DYNAMICALLY (ADR-0003 section 2/4),
  // rather than being pinned to a host-hours snapshot. ON reveals the per-event
  // editor and materializes an explicit workingHours on save. Initialized ON for
  // a loaded type that already carries workingHours.
  const [customHours, setCustomHours] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setState('loading');
    setLoadError(undefined);
    try {
      const res = await api.getAdminConfig();
      setConfig(cloneConfig(res.config));
      // Per-direction sync health for the status line (GET-only; the PUT
      // response carries no health, so the loaded snapshot is kept on save).
      setSyncHealth(res.syncHealth);
      setWeek(weekToForm(res.config.workingHours));
      // Seed each type's per-event hours form from its stored per-event schedule,
      // or the host schedule when it has none (a sensible starting point for a
      // type the host later marks private).
      const ew: Record<string, WeekForm> = {};
      const ch: Record<string, boolean> = {};
      for (const t of res.config.bookingTypes) {
        ew[t.id] = weekToForm(t.workingHours ?? res.config.workingHours);
        // A loaded type that already carries per-event hours initializes the
        // sub-toggle ON with those hours; otherwise it inherits (sub-toggle OFF).
        ch[t.id] = t.workingHours !== undefined;
      }
      setEventWeeks(ew);
      setCustomHours(ch);
      setState('ready');
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load settings.');
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function patch(p: Partial<HostConfig>): void {
    setConfig((c) => (c ? { ...c, ...p } : c));
    setSaved(false);
  }

  function updateType(id: string, p: Partial<BookingType>): void {
    setConfig((c) =>
      c
        ? { ...c, bookingTypes: c.bookingTypes.map((t) => (t.id === id ? { ...t, ...p } : t)) }
        : c,
    );
    setSaved(false);
  }

  function addType(): void {
    const id = newBookingTypeId();
    setConfig((c) =>
      c
        ? {
            ...c,
            bookingTypes: [
              ...c.bookingTypes,
              {
                id,
                name: 'New meeting',
                durationMin: 30,
                bufferBeforeMin: 0,
                bufferAfterMin: 0,
                description: '',
              },
            ],
          }
        : c,
    );
    // Seed a per-event hours form (from the host hours) so, if the host marks the
    // new type private, the schedule editor opens pre-filled rather than blank.
    setEventWeeks((ew) => ({ ...ew, [id]: { ...week } }));
    setSaved(false);
  }

  function removeType(id: string): void {
    setConfig((c) =>
      c ? { ...c, bookingTypes: c.bookingTypes.filter((t) => t.id !== id) } : c,
    );
    setEventWeeks((ew) => {
      const rest = { ...ew };
      delete rest[id];
      return rest;
    });
    setCustomHours((ch) => {
      const rest = { ...ch };
      delete rest[id];
      return rest;
    });
    setSaved(false);
  }

  // Toggle a booking type between public (listed) and private/unlisted (ADR-0003).
  // Private stores listed:false; public stores listed:true. Turning it on seeds a
  // per-event hours form from the host hours when one is not already present.
  function setPrivate(id: string, on: boolean): void {
    setConfig((c) =>
      c
        ? { ...c, bookingTypes: c.bookingTypes.map((t) => (t.id === id ? { ...t, listed: !on } : t)) }
        : c,
    );
    if (on) {
      setEventWeeks((ew) => (ew[id] ? ew : { ...ew, [id]: { ...week } }));
    }
    setSaved(false);
  }

  // Toggle a private event's "use custom hours" sub-switch (ADR-0003 section 2/4).
  // OFF (the default) leaves workingHours ABSENT so the event inherits the host
  // schedule dynamically; ON reveals the per-event editor and materializes an
  // explicit per-event schedule on save. Turning it ON (re)seeds the editor from
  // the CURRENT host hours as a starting point.
  function setCustomHoursFor(id: string, on: boolean): void {
    setCustomHours((ch) => ({ ...ch, [id]: on }));
    if (on) {
      setEventWeeks((ew) => ({ ...ew, [id]: { ...week } }));
    }
    setSaved(false);
  }

  // Edit one weekday of a private event's per-event hours form.
  function updateEventWeek(id: string, day: number, patch: Partial<DayForm>): void {
    setEventWeeks((ew) => {
      const current = ew[id] ?? {};
      const dayForm = current[day] ?? { enabled: false, start: '09:00', end: '17:00' };
      return { ...ew, [id]: { ...current, [day]: { ...dayForm, ...patch } } };
    });
    setSaved(false);
  }

  async function save(): Promise<void> {
    if (!config) return;
    setSaving(true);
    setSaveError(undefined);
    setSaved(false);

    const { hours, error } = formToWeek(week);
    if (error) {
      setSaveError(error);
      setSaving(false);
      return;
    }
    if (config.bookingTypes.length === 0) {
      setSaveError('Add at least one booking type.');
      setSaving(false);
      return;
    }
    // Build the cleaned booking types, carrying the private-event fields
    // (ADR-0003) THROUGH the save. This is load-bearing: zod strips unknown fields,
    // so an omitted slug/listed/workingHours would vanish silently on PUT. A
    // private type (listed === false) must have a valid, unique, non-reserved slug
    // and a per-event schedule; a public type keeps only an explicit listed flag it
    // already carried (kept additive for types that never had one).
    const usedSlugs: string[] = [];
    const cleanedTypes: BookingType[] = [];
    for (const t of config.bookingTypes) {
      const base: BookingType = {
        id: t.id,
        name: t.name.trim(),
        durationMin: t.durationMin,
        bufferBeforeMin: t.bufferBeforeMin,
        bufferAfterMin: t.bufferAfterMin,
      };
      const desc = t.description?.trim();
      if (desc) base.description = desc;

      // Carry the front buffer THROUGH the save (zod strips unknown fields, the
      // same slug/listed lesson as above): only materialize it when the toggle is
      // on and > 0, so an off/0/absent buffer serialises away entirely and is
      // byte-identical to today. The schema refine (front buffer < duration) is
      // the server-side guard; the editor hint warns before a bad save.
      if (t.frontBufferMin && t.frontBufferMin > 0) base.frontBufferMin = t.frontBufferMin;

      if (t.listed === false) {
        base.listed = false;
        const slug = (t.slug ?? '').trim();
        const slugErr = validateEventSlug(slug, usedSlugs);
        if (slugErr) {
          setSaveError(`${t.name.trim() || 'Private event'}: ${slugErr}`);
          setSaving(false);
          return;
        }
        usedSlugs.push(slug);
        base.slug = slug;
        // Only materialize a per-event schedule when the host opted into custom
        // hours for this event. When OFF (the default), workingHours stays ABSENT
        // so the event inherits the host schedule DYNAMICALLY (ADR-0003 section
        // 2/4): no snapshot, later host-hours edits and split days flow through.
        if (customHours[t.id]) {
          const form = eventWeeks[t.id] ?? weekToForm(t.workingHours ?? config.workingHours);
          const { hours: eventHours, error: eventError } = formToWeek(form);
          if (eventError) {
            setSaveError(`${t.name.trim() || 'Private event'}: ${eventError}`);
            setSaving(false);
            return;
          }
          base.workingHours = eventHours;
        }
      } else if (t.listed !== undefined) {
        base.listed = t.listed;
      }
      cleanedTypes.push(base);
    }
    // `sync` is carried through explicitly so the two-way mirror config
    // (calendar-sync.md section 4) is never dropped on PUT. Absent === feature
    // off, which serialises away as an omitted field.
    const next: HostConfig = {
      ...config,
      workingHours: hours,
      bookingTypes: cleanedTypes,
      sync: config.sync,
    };

    try {
      const res = await api.putAdminConfig(next);
      setConfig(cloneConfig(res.config));
      setWeek(weekToForm(res.config.workingHours));
      // Re-derive the per-event forms + custom-hours toggles from the saved
      // config so an event that just dropped its snapshot (custom OFF) reflects
      // inheritance, and one that kept explicit hours re-initializes ON.
      const ew: Record<string, WeekForm> = {};
      const ch: Record<string, boolean> = {};
      for (const t of res.config.bookingTypes) {
        ew[t.id] = weekToForm(t.workingHours ?? res.config.workingHours);
        ch[t.id] = t.workingHours !== undefined;
      }
      setEventWeeks(ew);
      setCustomHours(ch);
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setSaveError(err.message || 'Some settings are invalid. Please review and try again.');
      } else if (err instanceof ApiError && err.status === 401) {
        setSaveError('Your session expired. Please reload and sign in again.');
      } else {
        setSaveError('Could not save settings. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (state === 'loading') {
    return (
      <div className="state">
        <Spinner />
        <p>Loading settings…</p>
      </div>
    );
  }
  if (state === 'error' || !config) {
    return (
      <StateBlock
        title="Could not load settings"
        action={
          <button type="button" className="btn btn-ghost" onClick={() => void load()}>
            Try again
          </button>
        }
      >
        {loadError}
      </StateBlock>
    );
  }

  return (
    <div className="rise">
      <div className="admin-bar">
        <p className="page-desc" style={{ margin: 0 }}>
          Editing settings for <strong>{config.displayName}</strong>.
        </p>
        <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-small)' }}>
          {config.timezone}
        </span>
      </div>

      {/* Booking types */}
      <section className="admin-section">
        <h2>Booking types</h2>
        <p className="sec-desc">What people can book, with durations and buffers.</p>
        {config.bookingTypes.map((t) => {
          const isPrivate = t.listed === false;
          // Every OTHER private event's slug, for the live uniqueness check.
          const otherEventSlugs = config.bookingTypes
            .filter((o) => o.id !== t.id && o.listed === false)
            .map((o) => (o.slug ?? '').trim())
            .filter(Boolean);
          const slugError = isPrivate
            ? validateEventSlug((t.slug ?? '').trim(), otherEventSlugs)
            : null;
          return (
          <div className="bt-editor" key={t.id}>
            <div className="bt-head">
              <strong>{t.name || 'Untitled'}</strong>
              <button
                type="button"
                className="btn-link"
                style={{ color: 'var(--err)' }}
                onClick={() => removeType(t.id)}
                aria-label={`Remove ${t.name}`}
              >
                Remove
              </button>
            </div>
            <div className="bt-row">
              <div className="field">
                <label htmlFor={`name-${t.id}`}>Name</label>
                <input
                  id={`name-${t.id}`}
                  type="text"
                  value={t.name}
                  onChange={(e) => updateType(t.id, { name: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor={`dur-${t.id}`}>Duration (min)</label>
                <input
                  id={`dur-${t.id}`}
                  type="number"
                  min={1}
                  value={t.durationMin}
                  onChange={(e) =>
                    updateType(t.id, { durationMin: Math.max(1, Number(e.target.value) || 0) })
                  }
                />
              </div>
            </div>
            <div className="bt-row bt-row-3">
              <div className="field">
                <label htmlFor={`bb-${t.id}`}>Buffer before (min)</label>
                <input
                  id={`bb-${t.id}`}
                  type="number"
                  min={0}
                  value={t.bufferBeforeMin}
                  onChange={(e) =>
                    updateType(t.id, { bufferBeforeMin: Math.max(0, Number(e.target.value) || 0) })
                  }
                />
              </div>
              <div className="field">
                <label htmlFor={`ba-${t.id}`}>Buffer after (min)</label>
                <input
                  id={`ba-${t.id}`}
                  type="number"
                  min={0}
                  value={t.bufferAfterMin}
                  onChange={(e) =>
                    updateType(t.id, { bufferAfterMin: Math.max(0, Number(e.target.value) || 0) })
                  }
                />
              </div>
            </div>

            {/* Front buffer (ADR: optional per-type start buffer): dead time at
                the START of the meeting, inside the reserved slot. Off = a normal
                meeting (byte-identical to today). On = the meeting starts a few
                minutes into the reserved slot, so the host gets breathing room
                and the meeting still starts on time; the shifted start is shown
                to the customer. Distinct from the buffers above, which sit
                OUTSIDE the slot and never move the meeting start. */}
            <label className="switch-row" style={{ marginTop: 'var(--space-4)' }}>
              <input
                type="checkbox"
                role="switch"
                className="switch-input"
                checked={(t.frontBufferMin ?? 0) > 0}
                aria-label={`Front buffer (delayed start): ${t.name || 'Untitled'}`}
                onChange={(e) => updateType(t.id, { frontBufferMin: e.target.checked ? 5 : 0 })}
              />
              <span className="switch-label">Front buffer (delayed start)</span>
            </label>
            {(t.frontBufferMin ?? 0) > 0 ? (
              <div className="field">
                <label htmlFor={`fb-${t.id}`}>Front buffer (min)</label>
                <input
                  id={`fb-${t.id}`}
                  type="number"
                  min={0}
                  value={t.frontBufferMin ?? 0}
                  onChange={(e) =>
                    updateType(t.id, { frontBufferMin: Math.max(0, Number(e.target.value) || 0) })
                  }
                />
                <span className="field-hint">
                  Dead time at the start of the meeting, inside the reserved slot. Must be shorter
                  than the duration; it shifts the meeting start later (the customer sees the shifted
                  time), leaving the reserved slot unchanged.
                </span>
              </div>
            ) : null}

            <div className="field">
              <label htmlFor={`desc-${t.id}`}>Description</label>
              <input
                id={`desc-${t.id}`}
                type="text"
                value={t.description ?? ''}
                onChange={(e) => updateType(t.id, { description: e.target.value })}
                placeholder="Shown on the booking card"
              />
            </div>

            {/* Private event (ADR-0003): unlisted, with its own link slug and its
                own weekly hours. Off = a normal public type shown on the landing
                page. On = hidden from the list, reachable only at its own link. */}
            <label className="switch-row" style={{ marginTop: 'var(--space-4)' }}>
              <input
                type="checkbox"
                role="switch"
                className="switch-input"
                checked={isPrivate}
                aria-label={`Private event: ${t.name || 'Untitled'}`}
                onChange={(e) => setPrivate(t.id, e.target.checked)}
              />
              <span className="switch-label">
                {isPrivate ? 'Private event (unlisted)' : 'Private event'}
              </span>
            </label>
            {isPrivate ? (
              <>
                <div className="field">
                  <label htmlFor={`slug-${t.id}`}>Event link slug</label>
                  <input
                    id={`slug-${t.id}`}
                    type="text"
                    value={t.slug ?? ''}
                    onChange={(e) => updateType(t.id, { slug: sanitizeSlug(e.target.value) })}
                    placeholder="pt"
                    aria-invalid={slugError ? true : undefined}
                  />
                  {slugError ? (
                    <span className="field-error">{slugError}</span>
                  ) : (
                    <span className="field-hint">
                      Reachable at /{config.slug}/{(t.slug ?? '').trim() || 'your-slug'}. Never shown
                      on your public page.
                    </span>
                  )}
                </div>
                {/* "Use custom hours for this event" sub-toggle (ADR-0003
                    section 2/4). DEFAULT OFF: the event inherits the host
                    schedule dynamically and stores NO per-event workingHours.
                    ON reveals the per-event editor and pins explicit hours. */}
                <label className="switch-row" style={{ marginTop: 'var(--space-4)' }}>
                  <input
                    type="checkbox"
                    role="switch"
                    className="switch-input"
                    checked={customHours[t.id] ?? false}
                    aria-label={`Use custom hours for this event: ${t.name || 'Untitled'}`}
                    onChange={(e) => setCustomHoursFor(t.id, e.target.checked)}
                  />
                  <span className="switch-label">Use custom hours for this event</span>
                </label>
                {customHours[t.id] ? (
                  <EventHoursEditor
                    typeId={t.id}
                    week={eventWeeks[t.id] ?? {}}
                    timezone={config.timezone}
                    onChange={(day, patch) => updateEventWeek(t.id, day, patch)}
                  />
                ) : (
                  <p className="field-hint" style={{ marginTop: 'var(--space-2)' }}>
                    This event follows your general weekly working hours.
                  </p>
                )}
              </>
            ) : null}
          </div>
          );
        })}
        <button type="button" className="btn btn-ghost" onClick={addType}>
          + Add booking type
        </button>
      </section>

      {/* Working hours */}
      <section className="admin-section">
        <h2>Weekly working hours</h2>
        <p className="sec-desc">When you take meetings, in your timezone ({config.timezone}).</p>
        <div className="hours-grid">
          {Array.from({ length: 7 }, (_, d) => {
            const day = week[d] ?? { enabled: false, start: '09:00', end: '17:00' };
            return (
              <div className="hours-day" key={d}>
                <label className="hd-name">
                  <input
                    type="checkbox"
                    checked={day.enabled}
                    onChange={(e) => {
                      setWeek((w) => ({ ...w, [d]: { ...day, enabled: e.target.checked } }));
                      setSaved(false);
                    }}
                    style={{ marginRight: 6 }}
                    aria-label={`${DAY_NAMES[d]} enabled`}
                  />
                  {DAY_NAMES[d]?.slice(0, 3)}
                </label>
                {day.enabled ? (
                  <div className="hd-times">
                    <input
                      type="time"
                      value={day.start}
                      aria-label={`${DAY_NAMES[d]} start`}
                      onChange={(e) => {
                        setWeek((w) => ({ ...w, [d]: { ...day, start: e.target.value } }));
                        setSaved(false);
                      }}
                    />
                    <span style={{ color: 'var(--muted)' }}>to</span>
                    <input
                      type="time"
                      value={day.end}
                      aria-label={`${DAY_NAMES[d]} end`}
                      onChange={(e) => {
                        setWeek((w) => ({ ...w, [d]: { ...day, end: e.target.value } }));
                        setSaved(false);
                      }}
                    />
                  </div>
                ) : (
                  <span className="hd-off">Unavailable</span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Scheduling rules */}
      <section className="admin-section">
        <h2>Scheduling rules</h2>
        <p className="sec-desc">Notice, how far ahead people can book, and slot granularity.</p>
        <div className="field">
          <label htmlFor="min-notice">Minimum notice (min)</label>
          <input
            id="min-notice"
            type="number"
            min={0}
            value={config.minNoticeMin}
            onChange={(e) => patch({ minNoticeMin: Math.max(0, Number(e.target.value) || 0) })}
          />
          <span className="field-hint">How soon before a slot people can still book.</span>
        </div>
        <div className="field" style={{ marginTop: 'var(--space-4)' }}>
          <label htmlFor="rolling">Rolling window (days)</label>
          <input
            id="rolling"
            type="number"
            min={0}
            value={config.rollingWindowDays}
            onChange={(e) =>
              patch({ rollingWindowDays: Math.max(0, Number(e.target.value) || 0) })
            }
          />
          <span className="field-hint">How far into the future bookings are offered.</span>
        </div>
        <div className="field" style={{ marginTop: 'var(--space-4)' }}>
          <label htmlFor="grid">Slot granularity (min)</label>
          <input
            id="grid"
            type="number"
            min={1}
            value={config.gridMinutes}
            onChange={(e) => patch({ gridMinutes: Math.max(1, Number(e.target.value) || 0) })}
          />
          <span className="field-hint">Candidate start spacing, e.g. 15.</span>
        </div>
        <div className="field" style={{ marginTop: 'var(--space-4)' }}>
          <label htmlFor="slug">Booking page handle (slug)</label>
          <input
            id="slug"
            type="text"
            value={config.slug}
            onChange={(e) => patch({ slug: sanitizeSlug(e.target.value) })}
            placeholder="your-name"
          />
          <span className="field-hint">
            Changes your public booking link (the part after the domain). URL-safe
            only: lowercase letters, numbers, and hyphens.
          </span>
        </div>
        <div className="field" style={{ marginTop: 'var(--space-4)' }}>
          <label htmlFor="display-name">Display name</label>
          <input
            id="display-name"
            type="text"
            value={config.displayName}
            onChange={(e) => patch({ displayName: e.target.value })}
          />
        </div>
        <div className="field" style={{ marginTop: 'var(--space-4)' }}>
          <label htmlFor="tz">Timezone (IANA)</label>
          <input
            id="tz"
            type="text"
            value={config.timezone}
            onChange={(e) => patch({ timezone: e.target.value })}
            placeholder="Europe/Zurich"
          />
        </div>
      </section>

      {/* Connect calendars */}
      <ConnectSection config={config} />

      {/* Sync the two calendars (calendar-sync.md section 4) */}
      <SyncSection config={config} patch={patch} syncHealth={syncHealth} />

      {/* Sticky save bar */}
      <div className="save-bar">
        {saveError ? (
          <span className="field-error" style={{ marginRight: 'auto' }}>
            {saveError}
          </span>
        ) : saved ? (
          <span style={{ marginRight: 'auto', color: 'var(--ok)', fontWeight: 600 }}>
            Saved
          </span>
        ) : null}
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ---------------- Private-event per-event hours editor ----------------

/**
 * The per-event weekly working-hours editor for a private event (ADR-0003 section
 * 6). Mirrors the host schedule's one-window-per-weekday shape for consistency;
 * split days (multiple windows per weekday) are out of scope for v1, a conscious
 * limit. The aria-labels are scoped by the event id so they never collide with the
 * host hours grid's labels.
 */
function EventHoursEditor({
  typeId,
  week,
  timezone,
  onChange,
}: {
  typeId: string;
  week: WeekForm;
  timezone: string;
  onChange: (day: number, patch: Partial<DayForm>) => void;
}): JSX.Element {
  return (
    <div className="field">
      <label>Event working hours ({timezone})</label>
      <p className="field-hint" style={{ marginTop: 0 }}>
        When this event can be booked. Overrides your general hours, for this event
        only.
      </p>
      <div className="hours-grid">
        {Array.from({ length: 7 }, (_, d) => {
          const day = week[d] ?? { enabled: false, start: '09:00', end: '17:00' };
          return (
            <div className="hours-day" key={d}>
              <label className="hd-name">
                <input
                  type="checkbox"
                  checked={day.enabled}
                  onChange={(e) => onChange(d, { enabled: e.target.checked })}
                  style={{ marginRight: 6 }}
                  aria-label={`${DAY_NAMES[d]} enabled for event ${typeId}`}
                />
                {DAY_NAMES[d]?.slice(0, 3)}
              </label>
              {day.enabled ? (
                <div className="hd-times">
                  <input
                    type="time"
                    value={day.start}
                    aria-label={`${DAY_NAMES[d]} start for event ${typeId}`}
                    onChange={(e) => onChange(d, { start: e.target.value })}
                  />
                  <span style={{ color: 'var(--muted)' }}>to</span>
                  <input
                    type="time"
                    value={day.end}
                    aria-label={`${DAY_NAMES[d]} end for event ${typeId}`}
                    onChange={(e) => onChange(d, { end: e.target.value })}
                  />
                </div>
              ) : (
                <span className="hd-off">Unavailable</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Connect calendars ----------------

function ConnectSection({ config }: { config: HostConfig }): JSX.Element {
  const msConnection = config.connections.find((c) => c.providerId === 'numaco-m365');
  const googleConnection = config.connections.find((c) => c.providerId === 'scandit-gmail');

  return (
    <section className="admin-section">
      <h2>Connect calendars</h2>
      <p className="sec-desc">
        Numaco Meet only offers a slot when you are free across every connected calendar.
      </p>
      <div className="connect-grid">
        {/* Microsoft 365: connected automatically by signing in (ADR-0002 section 1). */}
        <div className="connect-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3>Microsoft 365</h3>
            {msConnection ? (
              <span className="pill pill-confirmed">
                {msConnection.role === 'read-write' ? 'Write target' : 'Read'}
              </span>
            ) : (
              <span className="pill pill-cancelled">Not connected</span>
            )}
          </div>
          <p>
            Your Numaco calendar. New bookings are written here and the customer is invited. It is
            connected when you sign in with Microsoft.
          </p>
        </div>

        {/* Google Calendar: an optional extra connect (session-guarded). */}
        <div className="connect-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3>Google Calendar</h3>
            {googleConnection ? (
              <span className="pill pill-confirmed">
                {googleConnection.role === 'read-write' ? 'Write target' : 'Read'}
              </span>
            ) : (
              <span className="pill pill-cancelled">Not connected</span>
            )}
          </div>
          <p>An extra calendar to block against. A private mirror event keeps it blocked, with no second invite.</p>
          <a className="btn btn-ghost" href={api.connectGoogleUrl()}>
            {googleConnection ? 'Reconnect' : 'Connect'} Google Calendar
          </a>
        </div>
      </div>
    </section>
  );
}

// ---------------- Sync calendars ----------------

// The closed set of sync-capable calendars, in a fixed order so the two
// direction legs render deterministically (calendar-sync.md section 3). The
// host's ACTUAL pair is derived from config.connections inside SyncSection;
// a calendar the host has not connected can never appear in a leg.
const SYNC_CALENDARS: readonly ConnectionId[] = ['numaco-m365', 'scandit-gmail'];
// The three detail modes a direction can take (calendar-sync.md section 4).
const SYNC_MODES: readonly SyncDirection['mode'][] = ['off', 'busy', 'full'];
const MODE_LABEL: Record<SyncDirection['mode'], string> = { off: 'Off', busy: 'Busy', full: 'Full' };
const CONNECTION_LABEL: Record<ConnectionId, string> = {
  'numaco-m365': 'Microsoft 365',
  'scandit-gmail': 'Google Calendar',
};
// Google's event-colour palette (the Calendar UI names, ids '1'..'11'). An
// empty selection serialises away and core falls back to '11' Tomato, the
// colour Max already reads as "Numaco" (calendar-sync.md section 3).
const GOOGLE_COLORS: readonly { id: string; name: string }[] = [
  { id: '1', name: 'Lavender' },
  { id: '2', name: 'Sage' },
  { id: '3', name: 'Grape' },
  { id: '4', name: 'Flamingo' },
  { id: '5', name: 'Banana' },
  { id: '6', name: 'Tangerine' },
  { id: '7', name: 'Peacock' },
  { id: '8', name: 'Graphite' },
  { id: '9', name: 'Blueberry' },
  { id: '10', name: 'Basil' },
  { id: '11', name: 'Tomato' },
];

/**
 * The two-way calendar-mirror control (calendar-sync.md section 4): a master
 * "Sync these two calendars" switch, off by default, plus a per-direction
 * Off/Busy/Full picker and the styling fields (busy label, plus the TARGET
 * platform's colour or category) for each leg of the pair, and the retention
 * knob. The section is GATED on both sync-capable calendars being connected:
 * anything less renders a pointer at the connect buttons instead of the form,
 * so a config the server would reject (a leg naming an unconnected calendar)
 * is unbuildable here. Enabling the master switch seeds BOTH directions to
 * 'busy' via the core `enableSyncDefaults` helper, and ONLY when no legs are
 * configured yet: re-enabling preserves the existing legs, exactly like the
 * off branch. 'full' stays a deliberate per-direction opt-in (section 8).
 * Binds through the same `patch`/`save` flow as the rest of the editor.
 */
function SyncSection({
  config,
  patch,
  syncHealth,
}: {
  config: HostConfig;
  patch: (p: Partial<HostConfig>) => void;
  syncHealth?: AdminConfigResponse['syncHealth'];
}): JSX.Element {
  // Off whenever the block is absent or the master switch is false.
  const enabled = config.sync?.enabled ?? false;
  const directions = config.sync?.directions ?? [];

  // The host's sync pair, derived from the calendars actually connected (never
  // a hardcoded pair): the fixed SYNC_CALENDARS order keeps the legs rendering
  // deterministically whatever order connections[] arrived in.
  const pair = SYNC_CALENDARS.filter((id) =>
    config.connections.some((c) => c.providerId === id),
  );
  const [pairA, pairB] = pair;

  if (pairA === undefined || pairB === undefined) {
    // Fewer than two sync-capable calendars (e.g. the default Microsoft-only
    // provisioning state): no master toggle, so the unsavable-form trap (a
    // sync leg naming a calendar the server knows is not connected) cannot
    // arise. The copy points at the connect buttons rendered just above.
    return (
      <section className="admin-section">
        <h2>Sync these two calendars</h2>
        <p className="sec-desc">
          Connect both calendars to mirror between them. Use the connect buttons in the Connect
          calendars section above; this section unlocks once Microsoft 365 and Google Calendar are
          both connected.
        </p>
      </section>
    );
  }

  // Narrowed aliases (the guard above proved both ends connected), so the
  // nested handlers below see plain ConnectionId values.
  const pairFrom: ConnectionId = pairA;
  const pairTo: ConnectionId = pairB;

  // Every sync patch spreads the existing block first, so fields owned by the
  // other controls (the sibling leg's styling, retentionDays) never get
  // dropped by an unrelated edit.
  function patchSync(p: Partial<NonNullable<HostConfig['sync']>>): void {
    patch({ sync: { enabled, directions, ...config.sync, ...p } });
  }

  function toggleMaster(on: boolean): void {
    if (on) {
      // Seed the core default posture (both legs 'busy') ONLY for a pair with
      // no configured legs, the spec's "newly enabled pair". Re-enabling a
      // previously configured pair preserves its legs, mirroring the off
      // branch; the "never 'full' by default" rule stays in core.
      const seeded = directions.length === 0 ? enableSyncDefaults(pairFrom, pairTo) : directions;
      patchSync({ enabled: true, directions: seeded });
    } else {
      // Keep the configured legs but flip the switch off, so the pair stops
      // mirroring without discarding the per-direction choices.
      patchSync({ enabled: false });
    }
  }

  function setMode(from: ConnectionId, to: ConnectionId, mode: SyncDirection['mode']): void {
    const exists = directions.some((d) => d.from === from && d.to === to);
    const nextDirections = exists
      ? directions.map((d) => (d.from === from && d.to === to ? { ...d, mode } : d))
      : [...directions, { from, to, mode }];
    patchSync({ directions: nextDirections });
  }

  // Upsert one styling field on a leg. An empty value DELETES the field, so a
  // cleared input serialises away and core's defaults ('Busy', Tomato,
  // 'Scandit') apply again instead of persisting an empty string.
  function setLegStyle(
    from: ConnectionId,
    to: ConnectionId,
    field: 'label' | 'googleColorId' | 'outlookCategory',
    value: string,
  ): void {
    const exists = directions.some((d) => d.from === from && d.to === to);
    const base: SyncDirection[] = exists
      ? directions
      : [...directions, { from, to, mode: 'off' as const }];
    const nextDirections = base.map((d) => {
      if (d.from !== from || d.to !== to) return d;
      const next: SyncDirection = { ...d };
      if (value) next[field] = value;
      else delete next[field];
      return next;
    });
    patchSync({ directions: nextDirections });
  }

  function setRetention(raw: string): void {
    const days = Math.min(30, Math.max(0, Math.round(Number(raw) || 0)));
    patchSync({ retentionDays: days });
  }

  const legs: { from: ConnectionId; to: ConnectionId }[] = [
    { from: pairFrom, to: pairTo },
    { from: pairTo, to: pairFrom },
  ];

  return (
    <section className="admin-section">
      <h2>Sync these two calendars</h2>
      <p className="sec-desc">
        Mirror your commitments both ways, so anyone scheduling you sees a blocked slot even outside
        Numaco Meet. Off by default; turning it on starts both directions on Busy (an opaque block).
        Full copies meeting detail into the other calendar, so it stays a per-direction choice.
        Turning sync off removes the mirrored events from both calendars within about half an hour.
      </p>

      <label className="switch-row">
        <input
          type="checkbox"
          role="switch"
          className="switch-input"
          checked={enabled}
          aria-label="Sync these two calendars"
          onChange={(e) => toggleMaster(e.target.checked)}
        />
        <span className="switch-label">{enabled ? 'On' : 'Off'}</span>
      </label>

      <div className="sync-dirs" aria-disabled={enabled ? undefined : true}>
        {legs.map((leg) => {
          const dir = directions.find((d) => d.from === leg.from && d.to === leg.to);
          const mode = dir?.mode ?? 'off';
          const groupLabel = `Detail from ${CONNECTION_LABEL[leg.from]} to ${CONNECTION_LABEL[leg.to]}`;
          const legId = `${leg.from}--${leg.to}`;
          return (
            <div className="sync-dir" key={`${leg.from}->${leg.to}`}>
              <div className="sync-dir-head">
                <span className="sync-dir-label">
                  {CONNECTION_LABEL[leg.from]} <span aria-hidden="true">→</span> {CONNECTION_LABEL[leg.to]}
                </span>
                <div className="pill-group" role="group" aria-label={groupLabel}>
                  {SYNC_MODES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`pill-toggle pill-${m}${mode === m ? ' is-active' : ''}`}
                      aria-pressed={mode === m}
                      disabled={!enabled}
                      onClick={() => setMode(leg.from, leg.to, m)}
                    >
                      {MODE_LABEL[m]}
                    </button>
                  ))}
                </div>
              </div>
              {/* Per-direction styling (calendar-sync.md section 4): the
                  busy-mode title plus the styling field for the TARGET
                  platform (a Google colour when the leg writes into Google
                  Calendar, an Outlook category when it writes into Microsoft
                  365). Empty means core's defaults. */}
              <div className="sync-dir-fields">
                <div className="field">
                  <label htmlFor={`sync-label-${legId}`}>Busy label</label>
                  <input
                    id={`sync-label-${legId}`}
                    type="text"
                    value={dir?.label ?? ''}
                    placeholder="Busy"
                    disabled={!enabled}
                    onChange={(e) => setLegStyle(leg.from, leg.to, 'label', e.target.value)}
                  />
                  <span className="field-hint">Mirror title in Busy mode.</span>
                </div>
                {leg.to === 'scandit-gmail' ? (
                  <div className="field">
                    <label htmlFor={`sync-color-${legId}`}>Google colour</label>
                    <select
                      id={`sync-color-${legId}`}
                      value={dir?.googleColorId ?? ''}
                      disabled={!enabled}
                      onChange={(e) => setLegStyle(leg.from, leg.to, 'googleColorId', e.target.value)}
                    >
                      <option value="">Default (Tomato)</option>
                      {GOOGLE_COLORS.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <span className="field-hint">Colour of the mirrors on Google Calendar.</span>
                  </div>
                ) : (
                  <div className="field">
                    <label htmlFor={`sync-category-${legId}`}>Outlook category</label>
                    <input
                      id={`sync-category-${legId}`}
                      type="text"
                      value={dir?.outlookCategory ?? ''}
                      placeholder="Scandit"
                      disabled={!enabled}
                      onChange={(e) => setLegStyle(leg.from, leg.to, 'outlookCategory', e.target.value)}
                    />
                    <span className="field-hint">Category stamped on the mirrors in Outlook.</span>
                  </div>
                )}
              </div>
              {/* One status line per direction (calendar-sync.md section 7,
                  health surfacing): the SOURCE connection's health stamps. */}
              <SyncHealthLine health={syncHealth?.[leg.from]} />
            </div>
          );
        })}
      </div>

      {/* Retention knob (calendar-sync.md section 8, retention policy): how
          long a mirror outlives its ended occurrence before the sweep reaps
          it. Absent means core's DEFAULT_RETENTION_DAYS, shown as the value. */}
      <div className="field sync-retention">
        <label htmlFor="retention-days">Keep ended mirrors (days)</label>
        <input
          id="retention-days"
          type="number"
          min={0}
          max={30}
          value={config.sync?.retentionDays ?? DEFAULT_RETENTION_DAYS}
          disabled={!enabled}
          onChange={(e) => setRetention(e.target.value)}
        />
        <span className="field-hint">
          Mirrors of ended events stay on the other calendar this many days (0 to 30) before they
          are removed.
        </span>
      </div>
    </section>
  );
}

/**
 * The per-direction sync status line (calendar-sync.md section 7, health
 * surfacing), one line, three states off the SOURCE connection's health stamps:
 * a live failure streak renders the stage-prefixed error as a warning
 * ("Error (3x): reconcile: …"); a healthy direction renders the last success
 * relatively ("Last synced 5 minutes ago"); no stamp at all means the
 * direction has never completed a pass ("Not yet synced"). The error fields
 * are history: after a recovery (consecutiveFailures reset to 0) the line
 * goes back to the success stamp.
 */
function SyncHealthLine({ health }: { health?: SyncHealth }): JSX.Element {
  if (health !== undefined && (health.consecutiveFailures ?? 0) > 0) {
    return (
      <p className="sync-health sync-health-error" role="status">
        Error ({health.consecutiveFailures}x): {health.lastErrorMessage ?? 'sync failed'}
      </p>
    );
  }
  if (health?.lastSuccessInstant !== undefined) {
    return (
      <p className="sync-health" role="status">
        Last synced {relativeTime(health.lastSuccessInstant)}
      </p>
    );
  }
  return (
    <p className="sync-health" role="status">
      Not yet synced
    </p>
  );
}
